import { useLayoutEffect, useRef, useState } from 'react';
import { format } from 'date-fns';

// Shared helpers for log components (InvoiceLog, OrderLog, RentalLog). Search
// runs against each entry's rendered text, so whatever an entry shows is
// searchable without keeping a separate list of fields in sync with the JSX.

// An entry matches when its text contains every word of the query, in any order.
export const matchesSearch = (text, searchQuery) => {
  const terms = searchQuery?.trim().toLowerCase().split(/\s+/).filter(Boolean) || [];
  const lowerText = (text || '').toLowerCase();
  return terms.every((term) => lowerText.includes(term));
};

// Numeric and long forms of a date (e.g. "10/2/2026 10/02/2026 10/2/26 October 2 2026"),
// so searching a date matches no matter which format the log displays.
export const getDateSearchText = (date) => {
  if (!date) return '';
  const parsed = new Date(date);
  if (Number.isNaN(parsed.getTime())) return '';
  return ['M/d/yyyy', 'MM/dd/yyyy', 'M/d/yy', 'MM/dd/yy', 'MMM d yyyy', 'MMMM d, yyyy']
    .map((fm) => format(parsed, fm))
    .join(' ');
};

// An entry whose component rendered nothing holds only LogSearchEntry's search-only span.
const isEmptyEntry = (el) => Array.from(el.children).every((child) => child.hasAttribute('data-log-search-text'));

// Hides entries (rendered through LogSearchEntry inside containerRef) whose text
// doesn't match the query. textContent includes LogSearchEntry's hidden search
// text and display:none content, so hidden entries can still match the next query.
export const useLogSearch = (searchQuery) => {
  const containerRef = useRef(null);
  const [result, setResult] = useState({ hiddenKeys: new Set(), matchCount: 0 });

  // No dependency list: entries' text changes whenever the log data re-renders, not just
  // when the query does. setResult keeps the previous state when nothing changed, so it settles.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useLayoutEffect(() => {
    const entries = Array.from(containerRef.current?.querySelectorAll('[data-log-entry]') || []);
    const hiddenKeys = new Set();
    if (searchQuery?.trim()) {
      entries.forEach((el) => {
        if (isEmptyEntry(el) || !matchesSearch(el.textContent, searchQuery)) hiddenKeys.add(el.dataset.logEntry);
      });
    }
    const matchCount = entries.length - hiddenKeys.size;
    setResult((prev) =>
      prev.matchCount === matchCount &&
      prev.hiddenKeys.size === hiddenKeys.size &&
      [...hiddenKeys].every((key) => prev.hiddenKeys.has(key))
        ? prev
        : { hiddenKeys, matchCount }
    );
  });

  return {
    containerRef,
    isHidden: (key) => result.hiddenKeys.has(String(key)),
    matchCount: result.matchCount,
  };
};

// Toggles the search bar's visibility, clearing the query on close and running
// onOpen (e.g. expanding the log) on open. Returns an event handler suitable
// for onClick/onClose props.
export const createSearchToggleHandler = (setShowSearchBar, setSearchQuery, onOpen) => (event) => {
  event?.stopPropagation();
  setShowSearchBar((prev) => {
    const next = !prev;
    if (next) onOpen?.();
    if (!next) setSearchQuery('');
    return next;
  });
};
